import { Suspense, lazy, useEffect, useState } from 'react';
import {
  ArrowRight,
  ShieldCheck,
  BadgeCheck,
  type LucideIcon,
} from 'lucide-react';

import { OpenAppButton } from '@/components/ui/open-app-dialog';
import { BookDemoButton } from '@/components/ui/book-demo-dialog';
import { Container } from '@/components/ui/container';
import { Badge } from '@/components/ui/badge';
import { BalanceCard, ApprovalCard } from '@/components/product-mocks';
import { hero } from '@/content/site';

// Code-split Three.js into its own chunk so it never blocks first paint.
const HeroScene = lazy(() =>
  import('@/components/three/HeroScene').then((m) => ({ default: m.HeroScene }))
);

const BADGE_ICONS: LucideIcon[] = [ShieldCheck, BadgeCheck];

/** The leading verb of the hero title, cycled with a typewriter effect. */
const TITLE_VERBS = ['Hold', 'Move', 'Automate'];

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReduced(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);
  return reduced;
}

/**
 * Types out each word, holds, deletes, and advances to the next — looping
 * forever, with a blinking caret. Honors prefers-reduced-motion by rendering
 * the first word statically (the full sentence is exposed to screen readers
 * separately, so the animation can stay aria-hidden).
 */
function Typewriter({ words }: { words: string[] }) {
  const reduced = usePrefersReducedMotion();
  const [index, setIndex] = useState(0);
  const [text, setText] = useState(words[0] ?? '');
  const [phase, setPhase] = useState<'pausing' | 'deleting' | 'typing'>(
    'pausing'
  );

  useEffect(() => {
    if (reduced) return;
    const word = words[index] ?? '';
    const delay = phase === 'pausing' ? 1800 : phase === 'typing' ? 80 : 45;
    const id = window.setTimeout(() => {
      if (phase === 'pausing') {
        setPhase('deleting');
      } else if (phase === 'deleting') {
        if (text.length > 1) {
          setText(word.slice(0, text.length - 1));
        } else {
          setText('');
          setIndex((i) => (i + 1) % words.length);
          setPhase('typing');
        }
      } else if (text.length < word.length) {
        setText(word.slice(0, text.length + 1));
      } else {
        setPhase('pausing');
      }
    }, delay);
    return () => window.clearTimeout(id);
  }, [phase, text, index, words, reduced]);

  // The caret holds steady while typing/deleting and only blinks once the word
  // has finished and is at rest (the "pausing" phase) — classic typewriter feel.
  const caretBlinks = !reduced && phase === 'pausing';
  return (
    <span
      className={`border-r-4 border-slate-500 text-slate-600 pr-1 ${
        caretBlinks ? 'animate-caret-blink' : ''
      }`}
    >
      {text}
    </span>
  );
}

export function Hero() {
  return (
    <section
      id="top"
      className="relative overflow-hidden bg-white pt-28 sm:pt-32 lg:pt-48"
    >
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div className="absolute inset-x-0 top-0 h-[560px] bg-grid-faint bg-[size:60px_60px] [mask-image:radial-gradient(60%_55%_at_50%_0%,black,transparent)]" />
      </div>

      <Container className="relative grid items-center gap-12 pb-20 lg:grid-cols-[1.40fr_0.98fr] lg:gap-10 lg:pb-36">
        {/* Copy — centered on mobile, left-aligned from sm up. */}
        <div className="flex flex-col items-center text-center sm:items-start sm:text-left">
          <div className="mb-6 flex flex-col flex-wrap items-center justify-center gap-2 sm:flex-row sm:justify-start">
            {hero.badges.map((label, i) => {
              const Icon = BADGE_ICONS[i] ?? ShieldCheck;
              return (
                <Badge key={label} variant="default">
                  <Icon className="size-3.5 text-ink-500" />
                  {label}
                </Badge>
              );
            })}
          </div>

          <h1 className="text-[2.5rem] font-medium leading-[1.08] tracking-tight text-slate-900 sm:text-5xl sm:leading-[1.05] xl:text-[3.5rem]">
            <span className="sr-only">
              Hold, move, and automate digital assets without getting hacked
            </span>
            <span aria-hidden="true">
              {/* On sm+ "[verb] digital assets" stays on one no-wrap line and
                  "without getting hacked" is forced onto the next line. On mobile
                  only the verb gets its own line; the rest flows/wraps naturally. */}
              <span className="sm:whitespace-nowrap">
                <span className="block sm:inline">
                  <Typewriter words={TITLE_VERBS} />
                </span>{' '}
                digital assets
              </span>
              <br className="hidden sm:block" />{' '}
              <span className="text-slate-900 sm:whitespace-nowrap">
                without getting hacked
              </span>
            </span>
          </h1>

          <p className="mt-6 max-w-xl text-pretty text-base leading-relaxed text-ink-500 sm:text-lg">
            {hero.subtitle}
          </p>

          <div className="mt-9 flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center">
            <BookDemoButton
              variant="primary"
              size="lg"
              analyticsLocation="hero"
            >
              {hero.primaryCta}
              <ArrowRight className="size-4 transition-transform duration-200 group-hover:translate-x-0.5" />
            </BookDemoButton>
            <OpenAppButton
              variant="secondary"
              size="lg"
              analyticsLocation="hero"
            >
              {hero.secondaryCta}
            </OpenAppButton>
          </div>
        </div>

        {/* Product-UI panel */}
        <HeroPanel />
      </Container>
    </section>
  );
}

function HeroPanel() {
  return (
    <div className="relative aspect-[4/3.6] w-full sm:aspect-[4/3]">
      {/* Soft ambient wash that dissolves at the edges — no hard bounding box */}
      <div
        aria-hidden
        className="absolute inset-0 bg-mesh [mask-image:radial-gradient(64%_64%_at_50%_42%,black,transparent)]"
      />

      {/* Motion graphic: its edges fade smoothly into the background behind it */}
      <Suspense fallback={null}>
        <HeroScene className="absolute inset-0 opacity-80 [mask-image:radial-gradient(72%_72%_at_50%_45%,black,transparent)]" />
      </Suspense>

      {/* Floating product mocks (kept crisp) */}
      <BalanceCard className="absolute left-[6%] top-[0%] w-[60%] max-w-[280px] animate-float-slow" />
      <ApprovalCard className="absolute bottom-[0%] right-[0%] w-[66%] max-w-[300px] animate-float" />
    </div>
  );
}
